/* global SERVER_URL */
import React, { useContext, useEffect, useState } from 'react';
import queryString from 'query-string';
import fetch from 'isomorphic-fetch';

import SnapshotView from './SnapshotView';
import { checkHttpStatus, parseJSON, possessive } from '../../utils';
import { QueryUrls, SnapshotGalleryContext } from '../../contexts/SnapshotGalleryContext';

export const galleryTypes = {
  FAVORITE: 'favorite',
  USER: 'user',
  CONTEST: 'contest',
  GALLERY: 'gallery',
  GROUP: 'group',
  ALL: 'all',
};

const displayName = (name) => {
  return name ? possessive(name) : 'User\'s';
};

const SnapshotFavoritesGallery = (props) => {
  const { setQueryId, setQueryType } = useContext(SnapshotGalleryContext);
  const { params: { favusername: favUsername } } = props;
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [avatar, setAvatar] = useState('');

  useEffect(() => {
    if (favUsername) {
      setQueryType(QueryUrls.USER_FAVORITES);
      setQueryId(favUsername);
      const requestParams = queryString.stringify({ username: favUsername });
      fetch(`${SERVER_URL}/get_user_info.json?${requestParams}`)
        .then(checkHttpStatus)
        .then(parseJSON)
        .then((response) => {
          const name = displayName(response.data.display_name);
          setTitle(`${name} Favorite Snapshots`);
          setAvatar(response.data.avatar_uri);
          setSlug(favUsername);
        })
        .catch((error) => {
          // Redirect to snapshots.
          console.error(error);
          return props.router.push('/snapshots');
        });
    }
  }, [favUsername]);
  return (
    <SnapshotView
      {...props}
      type={galleryTypes.FAVORITE}
      title={title}
      slug={slug}
      avatarUri={avatar}
    />
  );
};
const SnapshotUserGallery = (props) => {
  const { setQueryId, setQueryType } = useContext(SnapshotGalleryContext);
  const { params: { username} } = props;
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [avatar, setAvatar] = useState('');

  useEffect(() => {
    if (username) {
      setQueryType(QueryUrls.USER);
      setQueryId(username);
      const requestParams = queryString.stringify({ username });
      fetch(`${SERVER_URL}/get_user_info.json?${requestParams}`)
        .then(checkHttpStatus)
        .then(parseJSON)
        .then((response) => {
          const name = displayName(response.data.display_name);
          setTitle(`${name}  Snapshots`);
          setAvatar(response.data.avatar_uri);
          setSlug(username);
        })
        .catch((error) => {
          // Redirect to snapshots.
          console.error(error);
          return props.router.push('/snapshots');
        });
    }
  }, [username]);
  return (
    <SnapshotView
      {...props}
      type={galleryTypes.USER}
      title={title}
      slug={slug}
      avatarUri={avatar}
    />
  );
};
const SnapshotContestGallery = (props) => {
  const { setQueryId, setQueryType } = useContext(SnapshotGalleryContext);
  const { params: { contestslug: contestSlug } } = props;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [contestStatus, setContestStatus] = useState('');

  useEffect(() => {
    if (contestSlug) {
      setQueryType(QueryUrls.CONTEST);
      setQueryId(contestSlug);

      fetch(`${SERVER_URL}/snapshots/contest-info/${contestSlug}`)
        .then(checkHttpStatus)
        .then(parseJSON)
        .then(({ data }) => {
          const { title, public_gallery: publicGallery, description, status: contestStatus } = data;
          if (!publicGallery) return props.router.push('/snapshots');
          setTitle(`${title} Snapshots`);
          setDescription(description);
          setSlug(contestSlug);
          setContestStatus(contestStatus);
          return true;
        })
        .catch((error) => {
          // Redirect to snapshots.
          console.error(error);
          return props.router.push('/snapshots');
        });
    }
  }, [contestSlug]);

  return (
    <SnapshotView
      {...props}
      type={galleryTypes.CONTEST}
      title={title}
      slug={slug}
      description={description}
      contestStatus={contestStatus}
    />
  );
};
const SnapshotGallery = (props) => {
  const { setQueryId, setQueryType } = useContext(SnapshotGalleryContext);
  const { params: { galleryslug: gallerySlug } } = props;
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');

  useEffect(() => {
    if (gallerySlug) {
      setQueryType(QueryUrls.GALLERY);
      setQueryId(gallerySlug);

      fetch(`${SERVER_URL}/snapshots/gallery-info/${gallerySlug}`)
        .then(checkHttpStatus)
        .then(parseJSON)
        .then(({ data }) => {
          const { title } = data;
          setTitle(`${title} Snapshots`);
          setSlug(gallerySlug);
          return true;
        })
        .catch((error) => {
          // Redirect to snapshots.
          console.error(error);
          return props.router.push('/snapshots');
        });
    }
  }, [gallerySlug]);
  return (
    <SnapshotView {...props} type={galleryTypes.GALLERY} title={title} slug={slug} />
  );
};
const SnapshotGroupGallery = (props) => {
  const { channelsList = [], setQueryId, setQueryType } = useContext(SnapshotGalleryContext);
  const { params: { group } } = props;
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');

  function setGroupInfo(groupSlug) {
    setSlug(groupSlug);
    if (group === 'favorites') {
      setTitle('Fan Favorites');
      setQueryType(QueryUrls.FAN_FAVORITES);
      setQueryId(null);
      return;
    }
    if (groupSlug === 'camops') {
      setTitle('CamOp Snaps');
      setQueryType(QueryUrls.CAMOPS);
      setQueryId(null);
      return;
    }
    if (groupSlug === 'dj-of-nature-favorites') {
      setTitle('DJ of Nature\'s Favorites');
      setQueryType(QueryUrls.USER_FAVORITES);
      setQueryId('DJ-OF-NATURE');
      return;
    }
    const selectedChannel = channelsList?.find(c => c.slug === group);
    if (selectedChannel) {
      setTitle(selectedChannel.title);
      setQueryType(QueryUrls.CHANNEL);
      setQueryId(selectedChannel.id);
      return;
    }

    fetch(`${SERVER_URL}/snapshots/livecam/${groupSlug}`)
      .then(checkHttpStatus)
      .then(parseJSON)
      .then((response) => {
        setTitle(response.data.title);
        setQueryType(QueryUrls.LIVECAM);
        setQueryId(groupSlug);
      })
      .catch((error) => {
        // Feed doesn't exist. Redirect to snapshots.
        console.error(error);
        return props.router.push('/snapshots');
      });
  }

  useEffect(() => {
    if (group) {
      setGroupInfo(group);
    }
  }, [group]);

  return (
    <SnapshotView {...props} type={galleryTypes.GALLERY} title={title} slug={slug} />
  );
};

const SnapshotAllGallery = (props) => {
  const { setQueryId, setQueryType } = useContext(SnapshotGalleryContext);
  useEffect(() => {
    setQueryType(QueryUrls.ALL);
    setQueryId(null);
  }, []);
  return (
    <SnapshotView {...props} type={galleryTypes.ALL} title="All Snapshots" />
  );
};

export { SnapshotFavoritesGallery, SnapshotUserGallery, SnapshotContestGallery, SnapshotGallery, SnapshotGroupGallery, SnapshotAllGallery };

const SnapshotPageVariants = { SnapshotFavoritesGallery, SnapshotUserGallery, SnapshotContestGallery, SnapshotGallery, SnapshotGroupGallery, SnapshotAllGallery };
export default SnapshotPageVariants;
